import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { ChatMessages } from "./organisms/ChatMessages";
import { ChatFooter } from "./organisms/ChatFooter";
import { useChatState } from "../hooks/useChatState";
import { useExecutionDocuments } from "../../projects/hooks/useExecutionDocuments";
import { Document } from "./organisms/ChatFooter";
import { configService, AppConfig } from "../../../shared/services/configService";
import DocumentViewerModal from "../../../shared/components/common/DocumentViewerModal";
import { KnowledgeBaseWithDocuments } from "../../../types/navigationTypes";
import { StorageService } from "../../../shared/services/storageService";
import { useWallet, useFinancialStatus, InsufficientBalanceWarning, checkCanSendMessage } from "../../billing";
import ChatToolbar from "./molecules/ChatToolbar";
import { ConnectorsModal } from "./ConnectorsModal";
import {
  fetchAgentSkillPrefsBundle,
  deriveConnectorAvailabilityFromPrefs,
  buildConnectorDisplayNames,
} from "../../../shared/services/agentSkillPrefs";
import type { EnabledConnectors } from "../types/chatTypes";
import {
  KB_DOCUMENTS_CHANGED_EVENT,
  emitKbDocumentsChanged,
} from "../../projects/services/executionTemplatesEvents";
import { documentCacheService } from "../../../shared/services/documentCacheService";

interface ChatPanelProps {
  workpillId?: string;
  workpillExecutionId?: string | null;
  userId?: string;
  isDevelopment?: boolean; // Para forzar modo desarrollo vs producción
  chatState: ReturnType<typeof useChatState>;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ 
  workpillId, 
  workpillExecutionId,
  userId,
  isDevelopment,
  chatState
}) => {
  // Inicializar configuración centralizada al montar el componente
  useEffect(() => {
    const config: Partial<AppConfig> = {};
    
    // Si se proporcionan props, usar esos valores (modo producción)
    if (workpillId) config.workpillId = workpillId;
    if (workpillExecutionId) config.executionId = workpillExecutionId;
    if (userId) config.userId = userId;
    if (isDevelopment !== undefined) config.isDevelopment = isDevelopment;
    
    // Inicializar el servicio de configuración
    configService.initialize(config);
    configService.logCurrentConfig();
  }, [workpillId, workpillExecutionId, userId, isDevelopment]);

  // Obtener configuración actual desde el servicio
  const appConfig = configService.getConfig();
  
  // Desestructurar estado del chat desde props
  const {
    messages: normalMessages,
    isAgentMode,
    isLoading: normalIsLoading,
    sendMessage,
    stopGeneration,
    handleFeedback,
    updateChangesStatus, // 🆕 Para aceptar/rechazar cambios
    toggleAgentMode,
    isStreaming,
    streamingMessage,
    currentConversationId,
    currentStreamingThoughts,
    retryAgentMessage
  } = chatState;

  // Estado para el input del chat
  const [inputValue, setInputValue] = useState("");
  const [attachedDocuments, setAttachedDocuments] = useState<Document[]>([]);
  
  // Estado para el modo websearch
  const [isWebSearchMode, setIsWebSearchMode] = useState(false);
  
  // Estado para el visor de documentos
  const [viewingDocument, setViewingDocument] = useState<{ url: string; name: string } | null>(null);
  
  // Estado para las knowledge bases disponibles
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseWithDocuments[]>([]);
  const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(false);

  const [connectorsModalOpen, setConnectorsModalOpen] = useState(false);
  const [availableConnectors, setAvailableConnectors] = useState<EnabledConnectors>({
    euipoCaselawsearch: false,
    euipoTmclass: false,
    eurlex: false,
    cendoj: false,
    aepd: false,
    oepm: false,
    boe: false,
    tc: false,
    cnmc: false,
  });
  const [enabledConnectors, setEnabledConnectors] = useState<EnabledConnectors>({
    euipoCaselawsearch: false,
    euipoTmclass: false,
    eurlex: false,
    cendoj: false,
    aepd: false,
    oepm: false,
    boe: false,
    tc: false,
    cnmc: false,
  });
  const [connectorDisplayNames, setConnectorDisplayNames] = useState<
    Partial<Record<keyof EnabledConnectors, string>>
  >({});

  // 💰 Hooks de verificación de saldo/créditos
  const { wallet, isLoading: isLoadingWallet, refetch: refetchWallet } = useWallet(appConfig.userId);
  const financialStatus = useFinancialStatus(wallet, isLoadingWallet);

  const toggleWebSearchMode = () => {
    setIsWebSearchMode((v) => !v);
  };
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Hook para cargar documentos de la ejecución - usa lazy loading para no bloquear la UI
  const { documents: executionDocuments, loading: isLoadingDocs, error: docsError, load: loadDocuments, refetch: refetchDocuments } = useExecutionDocuments(
    appConfig.executionId,
    appConfig.workpillId,
    true // lazy = true: no cargar automáticamente
  );

  // KB links come from workpill_execution_knowledge_bases (execution id only); workpillId is cache-key only.
  const reloadKnowledgeBases = useCallback(async (forceRefresh = false) => {
    if (!appConfig.executionId) return;
    const cacheWorkpillSegment = appConfig.workpillId ?? "";
    try {
      setIsLoadingKnowledgeBases(true);
      if (forceRefresh) {
        documentCacheService.invalidateCache(appConfig.executionId, cacheWorkpillSegment);
      }
      const kbs = await documentCacheService.getDocuments(
        appConfig.executionId,
        cacheWorkpillSegment,
        forceRefresh
      );
      setKnowledgeBases(kbs);
    } catch (error) {
      console.error('Error loading knowledge bases:', error);
      setKnowledgeBases([]);
    } finally {
      setIsLoadingKnowledgeBases(false);
    }
  }, [appConfig.executionId, appConfig.workpillId]);

  // Cargar knowledge bases cuando cambia la ejecución
  useEffect(() => {
    reloadKnowledgeBases();
  }, [reloadKnowledgeBases]);

  // Forzar refresco de KBs + documentos cuando otro panel emite el evento
  useEffect(() => {
    const handleKbChanged = () => {
      reloadKnowledgeBases(true);
      refetchDocuments();
    };
    window.addEventListener(KB_DOCUMENTS_CHANGED_EVENT, handleKbChanged);
    return () => window.removeEventListener(KB_DOCUMENTS_CHANGED_EVENT, handleKbChanged);
  }, [reloadKnowledgeBases, refetchDocuments]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { catalog, userRows } = await fetchAgentSkillPrefsBundle();
        if (cancelled) return;
        setAvailableConnectors(deriveConnectorAvailabilityFromPrefs(catalog, userRows));
        setConnectorDisplayNames(buildConnectorDisplayNames(catalog));
      } catch (e) {
        console.error("Error loading connector preferences:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    setEnabledConnectors((prev) => ({
      euipoCaselawsearch:
        prev.euipoCaselawsearch && availableConnectors.euipoCaselawsearch,
      euipoTmclass: prev.euipoTmclass && availableConnectors.euipoTmclass,
      eurlex: prev.eurlex && availableConnectors.eurlex,
      cendoj: prev.cendoj && availableConnectors.cendoj,
      aepd: prev.aepd && availableConnectors.aepd,
      oepm: prev.oepm && availableConnectors.oepm,
      boe: prev.boe && availableConnectors.boe,
      tc: prev.tc && availableConnectors.tc,
      cnmc: prev.cnmc && availableConnectors.cnmc,
    }));
  }, [
    availableConnectors.euipoCaselawsearch,
    availableConnectors.euipoTmclass,
    availableConnectors.eurlex,
    availableConnectors.cendoj,
    availableConnectors.aepd,
    availableConnectors.oepm,
    availableConnectors.boe,
    availableConnectors.tc,
    availableConnectors.cnmc,
  ]);

  const messages = normalMessages;
  const isLoading = normalIsLoading;
  
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleSend = async () => {
    if (inputValue.trim()) {
      // Verificar que hay conversación
      if (!currentConversationId) {
        return;
      }

      // 💰 Verificar saldo ANTES de enviar (doble chequeo con datos frescos)
      try {
        if (!userId) return; // Should not happen given config init, but safety check
        
        // 1. Verificación rápida contra DB para asegurar que el dato es fresco
        const freshStatus = await checkCanSendMessage(userId);
        
        if (!freshStatus.canSendMessage) {
          console.warn('⛔ Mensaje bloqueado por servidor: saldo insuficiente', freshStatus);
          // Actualizar la UI inmediatamente
          await refetchWallet();
          return;
        }
      } catch (err) {
        // Si falla la verificación (error de red), permitimos intentar enviar y que falle el backend si es necesario,
        // o bloqueamos por seguridad. Bloquear es más seguro para evitar frustración.
        console.error('Error verifying balance before send:', err);
        // Opcional: mostrar toast de error
      }

      // 💰 Verificar saldo en memoria (por si acaso el hook ya lo tenía bloqueado)
      if (!financialStatus.canSendMessage) {
        console.warn('⛔ Mensaje bloqueado: saldo insuficiente (memoria)', financialStatus);
        return;
      }

      // Guardar el documento automáticamente al enviar mensaje (sin prompt de baseline ausente)
      StorageService.saveDocumentToSupabase({
        allowConflictDialog: true,
        promptIfNoBaseline: false,
      }).catch((err) => console.error("Error auto-saving on send:", err));
      
      // Guardar el mensaje y documentos antes de limpiar el input
      const messageToSend = inputValue.trim();
      const documentsForSending = attachedDocuments.map(doc => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        isCategory: doc.isCategory
      }));
      
      // Limpiar el input inmediatamente para mejor UX
      setInputValue("");
      setAttachedDocuments([]);
      
      // Enviar mensaje con documentos adjuntos
      await sendMessage(messageToSend, documentsForSending, {
        enableWebSearch: isWebSearchMode,
        enableEuipo:
          enabledConnectors.euipoCaselawsearch &&
          availableConnectors.euipoCaselawsearch,
        enableEuipoTmclass:
          enabledConnectors.euipoTmclass && availableConnectors.euipoTmclass,
        enableEurlex: enabledConnectors.eurlex && availableConnectors.eurlex,
        enableCendoj: enabledConnectors.cendoj && availableConnectors.cendoj,
        enableAepd: enabledConnectors.aepd && availableConnectors.aepd,
        enableOepm: enabledConnectors.oepm && availableConnectors.oepm,
        enableBoe: enabledConnectors.boe && availableConnectors.boe,
        enableTc: enabledConnectors.tc && availableConnectors.tc,
        enableCnmc: enabledConnectors.cnmc && availableConnectors.cnmc,
      });

      // 💰 Actualizar saldo después de enviar (para reflejar el gasto)
      // Lo hacemos sin await para no bloquear la UI
      refetchWallet().catch(err => console.error('Error updating wallet after send:', err));
      
      // Scroll al final después de enviar
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleStop = () => {
    const restoredMessage = stopGeneration();
    if (restoredMessage) {
      // Restaurar el mensaje del usuario en el input
      setInputValue(restoredMessage.content);
      // Restaurar documentos adjuntos si los hay
      if (restoredMessage.attachments && restoredMessage.attachments.length > 0) {
        setAttachedDocuments(restoredMessage.attachments);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDocumentsChange = (documents: Document[]) => {
    setAttachedDocuments(documents);
  };

  const handleViewDocument = (url: string, name: string) => {
    console.log('👀 Abriendo documento en visor:', name);
    setViewingDocument({ url, name });
  };

  // Función para añadir el documento actual al chat
  const handleAddDocumentToChat = () => {
    if (!viewingDocument) return;

    // Crear objeto de documento (necesitamos un ID, si no lo tenemos usamos la URL o generamos uno temporal)
    const newDoc: Document = {
      id: viewingDocument.url, // Usamos URL como ID temporal si no tenemos el real
      name: viewingDocument.name,
      type: 'pdf', // Asumimos PDF por ahora
      isCategory: false
    };

    // Evitar duplicados
    if (!attachedDocuments.some(d => d.id === newDoc.id || d.name === newDoc.name)) {
      setAttachedDocuments(prev => [...prev, newDoc]);
      // Opcional: Enfocar el input
      inputRef.current?.focus();
    }
  };

  // Callback cuando se sube un archivo exitosamente
  const handleUploadSuccess = async () => {
    await reloadKnowledgeBases(true);
    refetchDocuments();
    emitKbDocumentsChanged();
  };

  return (
    <div className="h-full flex flex-col bg-white font-sans overflow-hidden">
      {/* Chat-specific toolbar */}
      <ChatToolbar 
        onNewChat={chatState.startNewChat}
        onShowHistory={chatState.showHistory}
        showingHistory={chatState.showingHistory}
        hideHistory={chatState.hideHistory}
        conversationHistory={chatState.conversationHistory}
        loadConversation={chatState.loadConversation}
        currentConversationId={currentConversationId}
        workpillId={appConfig.workpillId}
        executionId={workpillExecutionId ?? appConfig.executionId}
        onDeleteChat={chatState.deleteChat}
      />
      
      {/* Área de mensajes con scroll */}
      <div className="flex-1 overflow-hidden min-h-0">
        <ChatMessages 
          messages={messages as any}
          onFeedback={handleFeedback}
          onUpdateChangesStatus={updateChangesStatus}
          isLoading={isLoading || isStreaming}
          streamingMessage={streamingMessage}
          currentStreamingThoughts={currentStreamingThoughts}
          onViewDocument={handleViewDocument}
          messagesEndRef={messagesEndRef}
          onRetryMessage={retryAgentMessage}
        />
      </div>
      
      {/* 💰 Aviso de saldo insuficiente */}
      <InsufficientBalanceWarning 
        financialStatus={financialStatus}
        userId={appConfig.userId || ''}
        isLoading={isLoadingWallet}
        onRefresh={refetchWallet}
      />
      
      {/* Footer fijo en la parte inferior */}
      <div className="shrink-0">
        <ChatFooter
          value={inputValue}
          onChange={handleInputChange}
          onSend={handleSend}
          onStop={handleStop}
          onKeyPress={handleKeyPress}
          loading={isLoading || isStreaming}
          disabled={isLoading || isStreaming || !financialStatus.canSendMessage}
          inputRef={inputRef}
          documentsEnabled={true}
          placeholder={
            !financialStatus.canSendMessage 
              ? "Please recharge to continue..." 
              : !currentConversationId 
                ? "Ask a question, draft the template, @ for documents, " 
                : "Type your message..."
          }
          onDocumentsChange={handleDocumentsChange}
          knowledgeBaseDocuments={executionDocuments}
          isLoadingKbDocs={isLoadingDocs}
          onLoadDocuments={loadDocuments}
          onRefetchDocuments={refetchDocuments}
          isAgentMode={isAgentMode}
          onToggleAgentMode={toggleAgentMode}
          isWebSearchMode={isWebSearchMode}
          onToggleWebSearchMode={toggleWebSearchMode}
          knowledgeBases={knowledgeBases}
          onUploadSuccess={handleUploadSuccess}
          onOpenConnectors={() => setConnectorsModalOpen(true)}
          enabledConnectors={enabledConnectors}
          availableConnectors={availableConnectors}
          workpillExecutionId={workpillExecutionId ?? appConfig.executionId}
        />
      </div>

      <ConnectorsModal
        isOpen={connectorsModalOpen}
        onClose={() => setConnectorsModalOpen(false)}
        availableConnectors={availableConnectors}
        enabledConnectors={enabledConnectors}
        onChange={setEnabledConnectors}
        connectorDisplayNames={connectorDisplayNames}
      />

      {/* Modal Visor de Documentos */}
      <DocumentViewerModal
        isOpen={!!viewingDocument}
        onClose={() => setViewingDocument(null)}
        documentUrl={viewingDocument?.url || null}
        documentName={viewingDocument?.name || ''}
      />
    </div>
  );
};

export default ChatPanel;
